import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  fetchPlayer, fetchGames, fetchEvents, fetchPlayers,
} from '../../utils/ifrData';
import {
  isActive, periodKeyOf, periodLabel, expectedScore, scoreFromResult, ageFrom, kFactor,
} from '../../utils/ifr';
import { titleByCode, PLAYER_TITLES } from '../../config/ifrTitles';
import { RATING_CATEGORIES, categoryInfo, fieldsFor } from '../../config/ratingCategories';
import { RS, TitleBadge } from './ratingUI';

// ── WASIFU RASMI WA MCHEZAJI ────────────────────────────────────────────
// Hadharani. Kila mchezo unaonyeshwa pamoja na hesabu yake. Mchezaji ana
// namba TATU tofauti (Classical/Rapid/Blitz) — tab inayochaguliwa
// inaonyesha historia na michezo ya kasi hiyo pekee.

export default function PlayerRating() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [player, setPlayer] = useState(null);
  const [games, setGames] = useState([]);
  const [names, setNames] = useState({});
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('history');
  const [cat, setCat] = useState(params.get('cat') || 'classical');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function load() {
    setLoading(true);
    try {
      const [p, gs, evs, all] = await Promise.all([
        fetchPlayer(id), fetchGames({ playerId: id }), fetchEvents(), fetchPlayers(),
      ]);
      setPlayer(p);
      setGames(gs.sort((a, b) => (b.periodKey || '').localeCompare(a.periodKey || '')));
      const em = {}; evs.forEach(e => { em[e.id] = e; });
      setEvents(em);
      const nm = {}; all.forEach(x => { nm[x.id] = x; });
      setNames(nm);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  if (loading) return <div style={RS.page}><div style={RS.wrap}><div style={RS.empty}>Inapakia…</div></div></div>;
  if (!player) return <div style={RS.page}><div style={RS.wrap}><div style={RS.empty}>Mchezaji hakupatikana.</div></div></div>;

  const F = fieldsFor(cat);
  const catInfo = categoryInfo(cat);
  const t = cat === 'classical' ? titleByCode(player.title) : null;
  const active = isActive(player[F.lastActivePeriod], periodKeyOf());
  const history = player[F.history] || [];
  const peak = history.length ? Math.max(...history.map(h => h.rating || 0)) : player[F.ifr];
  const catGames = games.filter(g => (g.category || 'classical') === cat);

  // Title ijayo (Classical pekee)
  const idx = PLAYER_TITLES.findIndex(x => x.code === player.title);
  const next = cat === 'classical' ? (idx > 0 ? PLAYER_TITLES[idx - 1] : (idx === -1 ? PLAYER_TITLES[PLAYER_TITLES.length - 1] : null)) : null;
  const normsFor = next
    ? (player.norms || []).filter(n => {
      const nt = titleByCode(n.title);
      return nt && nt.rating >= next.rating;
    }).length
    : 0;

  return (
    <div style={RS.page}>
      <div style={RS.wrap}>

        <div onClick={() => navigate('/rating')} style={RS.back}>← Orodha rasmi</div>

        <div style={RS.hero}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '8px' }}>
            {t && (
              <span style={{ ...RS.badge, background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: '12px', padding: '4px 9px' }}>
                {t.code}
              </span>
            )}
            <span style={{ fontSize: '11.5px', opacity: 0.75 }}>{t ? t.name : cat === 'classical' ? 'Bila title' : `${catInfo.label} — hakuna title`}</span>
          </div>
          <h1 style={RS.h1}>{player.fullName}</h1>
          <div style={RS.heroText}>
            {player.clubName || player.school || '—'}
            {player.dob ? ` · miaka ${ageFrom(player.dob)}` : ''}
            {!active && ' · hana shughuli'}
          </div>

          <div style={{ display: 'flex', gap: '20px', marginTop: '18px', flexWrap: 'wrap' }}>
            <Stat label={catInfo.shortLabel} value={player[F.established] ? player[F.ifr] : '—'} big />
            <Stat label="Kilele" value={peak || '—'} />
            <Stat label="Michezo" value={player[F.gamesRated] || 0} />
            {cat === 'classical' && <Stat label="Norms" value={(player.norms || []).length} />}
          </div>
        </div>

        {/* Vitufe vya kasi */}
        <div style={{ display: 'flex', gap: '7px', marginBottom: '14px' }}>
          {RATING_CATEGORIES.map(c => {
            const cf = fieldsFor(c.key);
            return (
              <button key={c.key} onClick={() => setCat(c.key)} style={{
                flex: 1, padding: '10px', borderRadius: '11px', cursor: 'pointer',
                border: `1px solid ${cat === c.key ? c.color : '#E8E4DA'}`,
                background: cat === c.key ? c.color : '#fff', color: cat === c.key ? '#fff' : '#6B7280',
                fontSize: '12px', fontWeight: 700, fontFamily: 'inherit',
              }}>
                {c.label}{player[cf.established] ? ` · ${player[cf.ifr]}` : ''}
              </button>
            );
          })}
        </div>

        {!player[F.established] && (
          <div style={{ ...RS.card, background: '#FFFBEB', border: '1px solid #FDE68A' }}>
            <div style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.75 }}>
              <strong>Bado hana rating rasmi ya {catInfo.label}.</strong> Ana michezo{' '}
              {(player[F.unratedGames] || []).length} kati ya 5 zinazohitajika dhidi ya
              wachezaji waliopimwa. Michezo yake yote inahifadhiwa na itatumika
              siku atakapofikisha kiwango.
            </div>
          </div>
        )}

        {cat !== 'classical' && (
          <div style={{ ...RS.card, background: '#FAF8F3', fontSize: '11.5px', color: '#6B7280' }}>
            {catInfo.label} haina Titles wala norms — kama FIDE, hizo ni za Classical pekee.
          </div>
        )}

        {/* Title ijayo (Classical pekee) */}
        {next && player[F.established] && (
          <div style={RS.card}>
            <h2 style={RS.h2}>Njia ya kufikia {next.name}</h2>
            <Progress label="Rating" current={player[F.ifr]} target={next.rating} color={next.color} />
            {next.norms > 0 && (
              <Progress
                label={`Norms (performance ${next.normPerformance}+)`}
                current={normsFor} target={next.norms} color={next.color} unit=""
              />
            )}
            <div style={{ fontSize: '11.5px', color: '#6B7280', marginTop: '10px', lineHeight: 1.7 }}>
              {next.description}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          {[
            { k: 'history', l: 'Mwenendo' },
            { k: 'games', l: `Michezo (${catGames.length})` },
            ...(cat === 'classical' ? [{ k: 'norms', l: `Norms (${(player.norms || []).length})` }] : []),
          ].map(x => (
            <button key={x.k} onClick={() => setTab(x.k)} style={{
              flex: 1, padding: '10px', borderRadius: '11px', cursor: 'pointer',
              border: `1px solid ${tab === x.k ? '#14452F' : '#E8E4DA'}`,
              background: tab === x.k ? '#14452F' : '#fff',
              color: tab === x.k ? '#fff' : '#6B7280',
              fontSize: '12.5px', fontWeight: 700, fontFamily: 'inherit',
            }}>{x.l}</button>
          ))}
        </div>

        {tab === 'history' && (
          <div style={RS.card}>
            {history.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#6B7280' }}>Bado hakuna kipindi kilichochapishwa cha {catInfo.label}.</div>
            ) : (
              <>
                <Chart history={history} />
                {[...history].reverse().map((h, i) => (
                  <div key={i} style={hRow}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#1A1A1A' }}>
                        {periodLabel(h.periodKey)}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                        {h.kind === 'initial' ? 'Rating ya kwanza' : `Michezo ${h.games}`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: '#14452F' }}>{h.rating}</div>
                      {h.change !== null && h.change !== undefined && (
                        <div style={{
                          fontSize: '11px', fontWeight: 700,
                          color: h.change > 0 ? '#059669' : h.change < 0 ? '#DC2626' : '#9CA3AF',
                        }}>
                          {h.change > 0 ? '+' : ''}{h.change}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {tab === 'games' && (
          <div style={RS.card}>
            {catGames.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#6B7280' }}>Bado hakuna mchezo rasmi wa {catInfo.label}.</div>
            ) : catGames.map(g => {
              const isWhite = g.whiteId === id;
              const oppId = isWhite ? g.blackId : g.whiteId;
              const opp = names[oppId];
              const score = scoreFromResult(g.result, isWhite ? 'white' : 'black');
              const myBefore = isWhite ? g.whiteRatingBefore : g.blackRatingBefore;
              const oppBefore = isWhite ? g.blackRatingBefore : g.whiteRatingBefore;
              const exp = (myBefore && oppBefore) ? expectedScore(myBefore, oppBefore) : null;
              const ev = events[g.eventId];
              return (
                <div key={g.id} style={hRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{
                        width: '10px', height: '10px', borderRadius: '3px', flexShrink: 0,
                        background: isWhite ? '#fff' : '#1A1A1A', border: '1px solid #9CA3AF',
                      }} />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#1A1A1A' }}>
                        {opp?.fullName || 'Mpinzani'}
                      </span>
                      {cat === 'classical' && opp?.title && <TitleBadge code={opp.title} />}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '3px' }}>
                      {ev?.name || '—'} · {periodLabel(g.periodKey)}
                      {oppBefore ? ` · mpinzani ${oppBefore}` : ''}
                    </div>
                    {exp !== null && (
                      <div style={{ fontSize: '10.5px', color: '#9CA3AF', marginTop: '3px' }}>
                        Matarajio {exp.toFixed(2)} · matokeo {score}
                      </div>
                    )}
                  </div>
                  <div style={{
                    fontSize: '13px', fontWeight: 800, whiteSpace: 'nowrap',
                    color: score === 1 ? '#059669' : score === 0 ? '#DC2626' : '#6B7280',
                  }}>
                    {score === 1 ? 'Ushindi' : score === 0 ? 'Ameshindwa' : 'Sare'}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'norms' && cat === 'classical' && (
          <div style={RS.card}>
            {(player.norms || []).length === 0 ? (
              <div style={{ fontSize: '13px', color: '#6B7280', lineHeight: 1.7 }}>
                Bado hana norm. Norm inapatikana kwa kucheza kwa kiwango cha
                title kwenye tukio moja rasmi.
              </div>
            ) : (player.norms || []).map((n, i) => {
              const nt = titleByCode(n.title);
              return (
                <div key={i} style={hRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <TitleBadge code={n.title} />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#1A1A1A' }}>
                        Norm ya {nt?.name || n.title}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '3px' }}>
                      {n.eventName} · {periodLabel(n.periodKey)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '15px', fontWeight: 800, color: '#14452F' }}>{n.performance}</div>
                    <div style={{ fontSize: '10px', color: '#9CA3AF' }}>performance</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={RS.footer}>
          Kila namba kwenye ukurasa huu inatokana na michezo iliyohifadhiwa.{' '}
          <span onClick={() => navigate('/rating/system')} style={RS.inlineLink}>
            Ona kanuni
          </span>.
        </div>

      </div>
    </div>
  );
}

function Stat({ label, value, big }) {
  return (
    <div>
      <div style={{ fontSize: big ? '30px' : '19px', fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      <div style={{ fontSize: '10.5px', opacity: 0.7, marginTop: '4px', letterSpacing: '0.5px' }}>{label}</div>
    </div>
  );
}

function Progress({ label, current, target, color, unit = '' }) {
  const pct = Math.min(100, Math.round(((current || 0) / target) * 100));
  return (
    <div style={{ marginBottom: '13px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
        <span style={{ color: '#6B7280', fontWeight: 600 }}>{label}</span>
        <span style={{ fontWeight: 800, color: pct >= 100 ? '#059669' : '#1A1A1A' }}>
          {current || 0}{unit} / {target}{unit}
        </span>
      </div>
      <div style={{ height: '8px', background: '#F0EEE7', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? '#059669' : color, borderRadius: '99px' }} />
      </div>
    </div>
  );
}

function Chart({ history }) {
  const pts = history.map(h => h.rating || 0);
  const min = Math.min(...pts) - 40;
  const max = Math.max(...pts) + 40;
  const range = Math.max(1, max - min);
  return (
    <div style={{ marginBottom: '18px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '90px' }}>
        {pts.map((v, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              height: `${((v - min) / range) * 80}px`,
              background: 'linear-gradient(180deg, #1F6B45, #14452F)',
              borderRadius: '4px 4px 0 0', minHeight: '4px',
            }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9CA3AF', marginTop: '6px' }}>
        <span>{periodLabel(history[0]?.periodKey)}</span>
        <span>{periodLabel(history[history.length - 1]?.periodKey)}</span>
      </div>
    </div>
  );
}

const hRow = {
  display: 'flex', alignItems: 'center', gap: '12px',
  padding: '12px 0', borderTop: '1px solid #F3F1EB',
};

export { kFactor };
